import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, FileText, LogOut, ExternalLink, Layers, KeyRound } from "lucide-react";
import { Logo } from "../components/Logo";
import { API, getToken, clearToken, authHeaders } from "../lib/api";
import ProductsTab from "../components/admin/ProductsTab";
import LinesTab from "../components/admin/LinesTab";
import ContentTab from "../components/admin/ContentTab";
import AccountTab from "../components/admin/AccountTab";

const TABS = [
  { id: "productos", label: "Productos", icon: Package },
  { id: "lineas", label: "Líneas", icon: Layers },
  { id: "contenido", label: "Contenido de la web", icon: FileText },
  { id: "cuenta", label: "Mi cuenta", icon: KeyRound },
];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("productos");
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/auth/me`, authHeaders())
      .then((res) => setUser(res.data))
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  if (checking)
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p className="font-mono text-xs uppercase tracking-widest text-neutral-400">Verificando sesión…</p>
      </div>
    );
  if (!user) return null;

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#E8E4D9] bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-view-site"
              className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2 text-xs font-bold text-neutral-600 hover:border-brand hover:text-brand"
            >
              <ExternalLink size={13} /> Ver sitio
            </a>
            <button
              onClick={() => {
                clearToken();
                navigate("/admin/login");
              }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-2 text-xs font-bold text-white hover:bg-neutral-700"
            >
              <LogOut size={13} /> Salir
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-2 px-5 pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                tab === t.id ? "bg-brand text-white" : "bg-white text-neutral-600 border border-[#E8E4D9]"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-8">
        {tab === "productos" && <ProductsTab />}
        {tab === "lineas" && <LinesTab />}
        {tab === "contenido" && <ContentTab />}
        {tab === "cuenta" && <AccountTab />}
      </main>
    </div>
  );
}
